export interface RaynetConfig {
  apiKey: string
  instanceName: string
  userName: string
  baseUrl?: string
}

export interface CreateLeadPayload {
  name: string
  email: string
  phone: string
  company?: string
  note?: string
  sourceId?: number
  topic?: string
  categoryId?: number
}

export interface RaynetLead {
  id: number
  name: string
  email: string
  phone: string
  company?: string
  note?: string
  createdAt: string
}

export interface RaynetContactSource {
  id: number
  code01: string
  code02: string | null
  strValue01: string | null
}

export interface RaynetLeadCategory {
  id: number
  code01: string
  code02: string | null
  strValue01: string | null
}

export interface RaynetError {
  code: string
  message: string
  details?: string
}

class RaynetClient {
  private config: RaynetConfig

  constructor(config: RaynetConfig) {
    this.config = {
      baseUrl: 'https://app.raynet.cz/api/v2',
      ...config,
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`
    
    // Basic Auth podle Raynet dokumentace
    const authString = btoa(`${this.config.userName}:${this.config.apiKey}`)
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${authString}`,
      'X-Instance-Name': this.config.instanceName,
      ...options.headers,
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          `Raynet API Error ${response.status}: ${
            errorData.message || response.statusText
          }`
        )
      }

      return await response.json()
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Neočekávaná chyba při komunikaci s Raynet API')
    }
  }

  async createLead(data: CreateLeadPayload): Promise<RaynetLead> {
    // Rozdělíme jméno na firstName a lastName
    const nameParts = data.name.trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''

    const payload: Record<string, unknown> = {
      // Základní povinná pole
      firstName: firstName,
      lastName: lastName,
    }

    // Poznámka - používáme notice místo description
    if (data.note?.trim()) {
      payload.notice = data.note.trim()
    }

    // contactInfo obsahuje email a telefon
    const contactInfo: Record<string, string> = {
      email: data.email
    }

    // Přidáme telefon pokud je zadán
    if (data.phone?.trim()) {
      contactInfo.tel1 = data.phone.trim()
      contactInfo.tel1Type = "Mobil"
    }

    payload.contactInfo = contactInfo

    // Firma/společnost - používáme companyName
    if (data.company?.trim()) {
      payload.companyName = data.company.trim()
    }

    // Přidáme sourceId pokud je zadán
    if (data.sourceId) {
      payload.contactSource = data.sourceId
    }

    // Přidáme topic pokud je zadán  
    if (data.topic?.trim()) {
      payload.topic = data.topic.trim()
    }

    // Přidáme categoryId pokud je zadán
    if (data.categoryId) {
      payload.category = data.categoryId
    }

    // Detailní logování před odesláním
    console.log('🚀 Odesílám lead do Raynet API:')
    console.log('📤 Payload:', JSON.stringify(payload, null, 2))
    console.log('🔧 Konfigurace:', {
      baseUrl: this.config.baseUrl,
      instanceName: this.config.instanceName,
      userName: this.config.userName,
      hasApiKey: !!this.config.apiKey
    })

    try {
      const result = await this.request<RaynetLead>('/lead', {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      
      console.log('✅ Lead úspěšně vytvořen v Raynetu:')
      console.log('📥 Odpověď:', JSON.stringify(result, null, 2))
      
      return result
    } catch (error) {
      console.error('❌ Chyba při vytváření leadu v Raynetu:', error)
      console.error('📤 Problematický payload byl:', JSON.stringify(payload, null, 2))
      throw error
    }
  }

  async getLeads(): Promise<RaynetLead[]> {
    const response = await this.request<{ data: RaynetLead[] }>('/lead')
    return response.data
  }

  async getContactSources(): Promise<RaynetContactSource[]> {
    const response = await this.request<{ data: RaynetContactSource[] }>('/contactSource')
    return response.data
  }

  async getLeadCategories(): Promise<RaynetLeadCategory[]> {
    const response = await this.request<{ data: RaynetLeadCategory[] }>('/leadCategory')
    return response.data
  }
}

// Factory function pro vytvoření klienta s env proměnnými
export function createRaynetClient(): RaynetClient {
  const apiKey = import.meta.env.VITE_RAYNET_API_KEY
  const instanceName = import.meta.env.VITE_RAYNET_INSTANCE
  const userName = import.meta.env.VITE_RAYNET_USER

  if (!apiKey || !instanceName || !userName) {
    throw new Error(
      'Chybí konfigurace Raynet API. Zkontrolujte environment proměnné VITE_RAYNET_API_KEY, VITE_RAYNET_INSTANCE a VITE_RAYNET_USER.'
    )
  }

  return new RaynetClient({
    apiKey,
    instanceName,
    userName,
  })
}

export { RaynetClient } 